import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { CheckCircle2, ArrowRight, ArrowLeft, MessageCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";

interface MerchantEnquiryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const BUSINESS_TYPES: Record<string, string[]> = {
  Travel: ["Tour operator", "Hotel", "Airline", "Other"],
  Education: ["School", "University", "Training centre", "Other"],
  Healthcare: ["Hospital", "Clinic", "Specialist centre", "Other"],
};

const EMIRATES_GCC = [
  "Abu Dhabi", "Dubai", "Sharjah", "Ajman", "Umm Al Quwain", "Ras Al Khaimah", "Fujairah",
  "Saudi Arabia", "Bahrain", "Kuwait", "Oman", "Qatar",
];

const MerchantEnquiryModal = ({ open, onOpenChange }: MerchantEnquiryModalProps) => {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    businessName: "",
    vertical: "",
    businessType: "",
    website: "",
    location: "",
    contactName: "",
    jobTitle: "",
    email: "",
    phone: "+971 ",
    preferredContact: "",
    avgTransaction: "",
    monthlyVolume: "",
    currentBnpl: "",
    heardFrom: "",
    notes: "",
  });

  const set = (field: string, value: string) => setForm(prev => ({ ...prev, [field]: value }));

  const canProceedStep1 = form.businessName.trim() && form.vertical;
  const canProceedStep2 = form.contactName.trim() && form.email.trim();

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const { error } = await supabase.functions.invoke("merchant-enquiry", {
        body: {
          businessName: form.businessName.trim(),
          vertical: form.vertical,
          businessType: form.businessType,
          website: form.website.trim(),
          location: form.location,
          contactName: form.contactName.trim(),
          jobTitle: form.jobTitle.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          preferredContact: form.preferredContact,
          avgTransaction: form.avgTransaction,
          monthlyVolume: form.monthlyVolume,
          currentBnpl: form.currentBnpl,
          heardFrom: form.heardFrom,
          notes: form.notes.trim(),
        },
      });

      if (error) throw error;
      setStep(4);
    } catch (err) {
      console.error("Merchant enquiry error:", err);
      toast({
        title: "Submission failed",
        description: "Please try again or contact us directly at hello@flexaura.me",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    // Reset after animation
    setTimeout(() => {
      setStep(1);
      setForm({
        businessName: "", vertical: "", businessType: "", website: "", location: "",
        contactName: "", jobTitle: "", email: "", phone: "+971 ", preferredContact: "",
        avgTransaction: "", monthlyVolume: "", currentBnpl: "", heardFrom: "", notes: "",
      });
    }, 300);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        {step < 4 && (
          <DialogHeader>
            <div className="flex items-center justify-between mb-2">
              <DialogTitle className="font-heading text-xl text-primary">
                Apply to be listed
              </DialogTitle>
              <span className="text-sm font-body text-muted-foreground">
                Step {step} of 3
              </span>
            </div>
            <Progress value={(step / 3) * 100} className="h-1.5" />
          </DialogHeader>
        )}

        {/* STEP 1 — Business Details */}
        {step === 1 && (
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label htmlFor="eq-biz">Business name *</Label>
              <Input id="eq-biz" value={form.businessName} onChange={e => set("businessName", e.target.value)} placeholder="e.g. Horizon Travel LLC" maxLength={200} />
            </div>

            <div className="space-y-2">
              <Label>Vertical *</Label>
              <Select value={form.vertical} onValueChange={v => { set("vertical", v); set("businessType", ""); }}>
                <SelectTrigger><SelectValue placeholder="Select vertical" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Travel">Travel</SelectItem>
                  <SelectItem value="Education">Education</SelectItem>
                  <SelectItem value="Healthcare">Healthcare</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {form.vertical && (
              <div className="space-y-2">
                <Label>Business type</Label>
                <Select value={form.businessType} onValueChange={v => set("businessType", v)}>
                  <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    {BUSINESS_TYPES[form.vertical]?.map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="eq-web">Website URL</Label>
              <Input id="eq-web" type="url" value={form.website} onChange={e => set("website", e.target.value)} placeholder="https://yourcompany.com" maxLength={500} />
            </div>

            <div className="space-y-2">
              <Label>Emirates / Country</Label>
              <Select value={form.location} onValueChange={v => set("location", v)}>
                <SelectTrigger><SelectValue placeholder="Select location" /></SelectTrigger>
                <SelectContent>
                  {EMIRATES_GCC.map(l => (
                    <SelectItem key={l} value={l}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button onClick={() => setStep(2)} disabled={!canProceedStep1} className="w-full" variant="default">
              Next <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        )}

        {/* STEP 2 — Contact Details */}
        {step === 2 && (
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label htmlFor="eq-contact">Contact name *</Label>
              <Input id="eq-contact" value={form.contactName} onChange={e => set("contactName", e.target.value)} placeholder="Full name" maxLength={200} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="eq-title">Job title</Label>
              <Input id="eq-title" value={form.jobTitle} onChange={e => set("jobTitle", e.target.value)} placeholder="e.g. Head of Finance" maxLength={200} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="eq-email">Email address *</Label>
              <Input id="eq-email" type="email" value={form.email} onChange={e => set("email", e.target.value)} placeholder="you@company.com" maxLength={320} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="eq-phone">Phone number</Label>
              <Input id="eq-phone" type="tel" value={form.phone} onChange={e => set("phone", e.target.value)} placeholder="+971 50 123 4567" maxLength={20} />
            </div>

            <div className="space-y-2">
              <Label>Preferred contact method</Label>
              <Select value={form.preferredContact} onValueChange={v => set("preferredContact", v)}>
                <SelectTrigger><SelectValue placeholder="Select method" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Email">Email</SelectItem>
                  <SelectItem value="WhatsApp">WhatsApp</SelectItem>
                  <SelectItem value="Phone call">Phone call</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-3">
              <Button onClick={() => setStep(1)} variant="outline" className="flex-1">
                <ArrowLeft className="mr-2 w-4 h-4" /> Back
              </Button>
              <Button onClick={() => setStep(3)} disabled={!canProceedStep2} className="flex-1" variant="default">
                Next <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* STEP 3 — Commercial Details */}
        {step === 3 && (
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>Average transaction value</Label>
              <Select value={form.avgTransaction} onValueChange={v => set("avgTransaction", v)}>
                <SelectTrigger><SelectValue placeholder="Select range" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Under AED 5K">Under AED 5K</SelectItem>
                  <SelectItem value="AED 5-15K">AED 5–15K</SelectItem>
                  <SelectItem value="AED 15-30K">AED 15–30K</SelectItem>
                  <SelectItem value="AED 30K+">AED 30K+</SelectItem>
                  <SelectItem value="Varies">Varies</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Monthly booking volume (approx.)</Label>
              <Select value={form.monthlyVolume} onValueChange={v => set("monthlyVolume", v)}>
                <SelectTrigger><SelectValue placeholder="Select volume" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Under 50">Under 50</SelectItem>
                  <SelectItem value="50-200">50–200</SelectItem>
                  <SelectItem value="200-500">200–500</SelectItem>
                  <SelectItem value="500+">500+</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Currently using BNPL?</Label>
              <Select value={form.currentBnpl} onValueChange={v => set("currentBnpl", v)}>
                <SelectTrigger><SelectValue placeholder="Select option" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Yes — Tabby">Yes — Tabby</SelectItem>
                  <SelectItem value="Yes — Tamara">Yes — Tamara</SelectItem>
                  <SelectItem value="Yes — Other">Yes — Other</SelectItem>
                  <SelectItem value="No">No</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>How did you hear about Flexaura?</Label>
              <Select value={form.heardFrom} onValueChange={v => set("heardFrom", v)}>
                <SelectTrigger><SelectValue placeholder="Select source" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Google">Google</SelectItem>
                  <SelectItem value="LinkedIn">LinkedIn</SelectItem>
                  <SelectItem value="Referral">Referral</SelectItem>
                  <SelectItem value="WhatsApp">WhatsApp</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="eq-notes">Additional notes</Label>
              <Textarea id="eq-notes" value={form.notes} onChange={e => set("notes", e.target.value)} placeholder="Anything else we should know..." rows={3} maxLength={2000} />
            </div>

            <div className="flex gap-3">
              <Button onClick={() => setStep(2)} variant="outline" className="flex-1">
                <ArrowLeft className="mr-2 w-4 h-4" /> Back
              </Button>
              <Button onClick={handleSubmit} disabled={isSubmitting} className="flex-1 bg-primary hover:bg-primary/90">
                {isSubmitting ? "Submitting…" : "Submit Application"}
              </Button>
            </div>

            {/* WhatsApp fallback */}
            <div className="text-center pt-2 border-t border-border">
              <a
                href="https://wa.me/971551088212"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary smooth-transition"
              >
                <MessageCircle className="w-4 h-4" />
                Prefer to chat? Message us on WhatsApp →
              </a>
            </div>
          </div>
        )}

        {/* STEP 4 — Confirmation */}
        {step === 4 && (
          <div className="py-8 text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-8 h-8 text-primary" />
            </div>
            <h3 className="text-2xl font-heading text-primary">Application received!</h3>
            <p className="text-muted-foreground font-body">
              Our partnerships team will be in touch within 2 business days.
            </p>
            <button
              onClick={() => { handleClose(); navigate("/"); }}
              className="text-sm text-primary hover:underline font-body"
            >
              In the meantime, explore how Flexaura works →
            </button>
            <div className="pt-4">
              <Button variant="outline" onClick={handleClose}>Close</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MerchantEnquiryModal;
